import { useQc } from './context';
import { useCustomPresets } from './customPresetsContext';
import { resolveRuleset } from './customPresets';
import { thresholdLines, verificationStatus } from './thresholds';
import './QcThresholds.css';

/**
 * Only http/https references may render as a live link. A custom/4o9-imported
 * preset with a `javascript:`/`data:` reference must never become a click-to-run
 * link in the vendor's tooling (spec §7); the store already strips `reference`
 * from custom presets, and this is the display-layer defense-in-depth for any
 * future path that reaches this sink.
 */
function safeHref(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The active QC preset's threshold sheet (cue-forge-n3e.8), with an honest
 * verified/draft badge and a link to the cited platform spec. Moved out of the
 * standalone QC route into Studio when QC folded in (cue-forge-ve7): a QC pass is
 * only as credible as the limits behind it, so surface them beside the Run control
 * and say honestly whether they are verified against the cited spec. Collapsed by
 * default; native <details> gives a free keyboard toggle + a non-colour shape cue.
 * A custom preset (cue-forge-3r9) resolves to its unverified ruleset here, so the
 * Draft caption holds automatically and no "Verified" badge is possible.
 */
export function QcThresholds() {
  const { preset } = useQc();
  const { presets: customs } = useCustomPresets();
  const ruleset = resolveRuleset(preset, customs);
  const verification = verificationStatus(ruleset);
  const thresholds = thresholdLines(ruleset);
  const reference = safeHref(verification.reference);

  return (
    <details className="cf-qc__thresholds">
      <summary className="cf-qc__thresholds-summary">
        <span>Thresholds</span>
        <span
          className={`cf-qc__verbadge cf-qc__verbadge--${
            verification.verified ? 'verified' : 'draft'
          }`}
        >
          {verification.label}
        </span>
      </summary>
      <p className="cf-qc__vernote">
        {verification.caption}
        {reference && (
          <>
            {' '}
            <a className="cf-qc__verref" href={reference} target="_blank" rel="noreferrer">
              View reference
            </a>
          </>
        )}
      </p>
      <table className="cf-qc__threshold-table">
        <thead>
          <tr>
            <th scope="col">Rule</th>
            <th scope="col">Limit</th>
          </tr>
        </thead>
        <tbody>
          {thresholds.map((t) => (
            <tr key={t.ruleId}>
              <td>{t.label}</td>
              <td className="cf-mono">{t.limit}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}
